import type { TeamMember } from '@commissionsight/sdk';
import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { Button, Card, ICONS, Icon, inputClass } from '../components/ui.js';

/** Account settings: team management. (The activity/audit log moved to its own
 *  page — see pages/Activity.tsx, linked from the account menu.) */
export function TeamActivity() {
  const { client } = useAuth();
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTeam = () =>
    client
      .listTeam()
      .then((r) => setTeam(r.data))
      .catch(() => {});

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    loadTeam();
  }, [client]);

  const invite = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await client.inviteTeammate(e);
      setNote(res.status === 'already_member' ? `${e} is already on the team.` : `Invited ${e}.`);
      setEmail('');
      await loadTeam();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not invite');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    setError(null);
    try {
      await client.removeTeammate(userId);
      await loadTeam();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove member');
    }
  };

  return (
    <Card>
      <div className="mb-1 flex items-center gap-2">
        <Icon path={ICONS.users} size={18} className="text-neutral-500" />
        <h2 className="font-semibold">Team</h2>
      </div>
      <p className="mb-4 text-sm text-neutral-500">
        Invite teammates by email — they sign in with a one-time code, no password needed.
      </p>
      <div className="mb-4 flex gap-2">
        <input
          className={inputClass}
          type="email"
          placeholder="teammate@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button onClick={invite} loading={busy}>
          Invite
        </Button>
      </div>
      {note && (
        <p className="mb-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{note}</p>
      )}
      {error && <p className="mb-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {team.map((m) => (
          <div key={m.id} className="flex items-center justify-between py-2.5 text-sm">
            <div>
              <div className="font-medium">{m.email}</div>
              <div className="text-xs text-neutral-400">{m.role}</div>
            </div>
            {team.length > 1 ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                onClick={() => remove(m.id)}
              >
                Remove
              </Button>
            ) : (
              <span
                title="An account must keep at least one member — invite another teammate first."
                className="cursor-not-allowed select-none text-xs text-neutral-300 dark:text-neutral-600"
              >
                Remove
              </span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
